import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleNames } from '../../../Config/constants';
import { dataService } from '../../../Services/data/data.service';
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, TextField, Box, ToggleButtonGroup, ToggleButton, InputAdornment } from '@mui/material';
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { X, Lock, Eye, EyeOff } from 'lucide-react';
import Button from '../../Shared/Buttons/Button';

export default function UserCrudDialog({ dialogOpen, closeDialog, ...props }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const auth = getAuth();

    const [isExistingUser, setIsExistingUser] = useState(false);
    const [generalError, setGeneralError] = useState(null);

    const [firstName, setFirstName] = useState(null);
    const [firstNameError, setFirstNameError] = useState(null);

    const [surName, setSurName] = useState(null);
    const [surNameError, setSurNameError] = useState(null);

    const [email, setEmail] = useState(null);
    const [emailError, setEmailError] = useState(null);

    const [roles, setRoles] = useState([3]);
    const [rolesError, setRolesError] = useState(null);

    const [password, setPassword] = useState(null);
    const [passwordError, setPasswordError] = useState(null);

    const [confirmPassword, setConfirmPassword] = useState(null);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);


    const changeFirstName = (e) => {
        setFirstName(e.target.value);
        if (e.target.value.length > 0) {
            setFirstNameError(null);
        }
    }

    const changeSurName = (e) => {
        setSurName(e.target.value);
        if (e.target.value.length > 0) {
            setSurNameError(null);
        }
    }

    const changeEmail = (e) => {
        setEmail(e.target.value);
        if (e.target.value.length > 0 && validateEmail(e.target.value)) {
            setEmailError(null);
        }
    }

    const changeRole = (e) => {
        var intValue = parseInt(e.target.value);
        var arrRoles = JSON.parse(JSON.stringify(roles));
        if (arrRoles.includes(intValue)) {
            //Remove the roleId from the roleArray
            arrRoles = arrRoles.filter(item => item !== intValue);

        } else {
            //Add role to the roleArray
            setRolesError(null);
            arrRoles.push(intValue);
        }
        setRoles(arrRoles);
    }

    const changePassword = (e) => {
        setPassword(e.target.value);
    }

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = () => setShowPassword(!showPassword);

    const changeConfirmPassword = (e) => {
        setConfirmPassword(e.target.value);
    }

    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleMouseDownConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const checkFields = () => {
        var disabled = false;
        if (firstName === "" || firstName === null || firstName === undefined) {
            if (!isExistingUser) {
                disabled = true;
            }
        }
        if (surName === "" || surName === null || surName === undefined) {
            if (!isExistingUser) {
                disabled = true;
            }
        }
        if (email === "" || email === null || email === undefined || !validateEmail(email)) {
            disabled = true;
        }
        return disabled;
    }

    const validateEmail = (strEmail) => {// eslint-disable-next-line
        const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return regex.test(strEmail);
    }

    const validateFields = () => {
        setGeneralError(null);
        setFirstNameError(null);
        setSurNameError(null);
        setEmailError(null);
        var validationGood = true;
        if (!isExistingUser) {
            if (firstName === "" || firstName === null || firstName === undefined) {
                validationGood = false;
                setFirstNameError(t('userCrudDialog.firstNameRequired'));
            }
            if (surName === "" || surName === null || surName === undefined) {
                validationGood = false;
                setSurNameError(t('userCrudDialog.surnameRequired'));
            }
        }
        if (email === "" || email === null || email === undefined) {
            validationGood = false;
            setEmailError(t('userCrudDialog.emailRequired'));
        } else {
            if (!validateEmail(email)) {
                validationGood = false;
                setEmailError(t('userCrudDialog.emailInvalid'));
            }
        }

        if (roles === null || roles === null || roles.length === 0) {
            validationGood = false;
            setRolesError(t('userCrudDialog.rolesRequired'));
        }
        // if (!isExistingUser) {
        //     if (password === "" || password === null || password === undefined) {
        //         validationGood = false;
        //     } else {
        //         if (password.length < 8) {
        //             setPasswordError("Password must be at least 8 characters");
        //         }
        //     }
        //     if (confirmPassword === "" || confirmPassword === null || confirmPassword === undefined) {
        //         validationGood = false;
        //     }
        //     if ((confirmPassword !== "" && confirmPassword !== null && confirmPassword !== undefined) && (password !== "" && password !== null && password !== undefined)) {
        //         if (confirmPassword !== password) {
        //             validationGood = false;
        //             setPasswordError("The two passwords must match!");
        //         }
        //     }
        // }

        return validationGood;
    }

    const handleSaveUser = async () => {
        try {
            if (!validateFields()) return;

            if (isExistingUser) {
                // Updating an existing user
                const userObject = {
                    email: email,
                    roles: roles,
                    companyId: props.companyId,
                    insertedByUserId: props.userId,
                };

                try {
                    const response = await dataService.addUser(userObject);
                    if (response.success) {
                        window.location.reload();
                    } else {
                        setGeneralError(response.message);
                    }
                } catch (error) {
                    console.error("Error updating user:", error);
                }

            } else {
                // Creating a new user
                const userObject = {
                    name: surName,
                    firstName: firstName,
                    email: email,
                    fireBaseId: '',
                    roles: roles,
                    insertedByUserId: props.userId,
                    companyId: props.companyId
                };

                try {
                    // Use a secondary Firebase app so the admin's session is not replaced
                    const secondaryApp = initializeApp(auth.app.options, "secondaryAppForUserCreation");
                    const secondaryAuth = getAuth(secondaryApp);

                    try {
                        // Step 1: Create user in Firebase (on secondary app, so admin stays signed in)
                        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, "gX7!vZr@4pQ#dLm9");
                        userObject.fireBaseId = userCredential.user.uid;

                        // Sign out from secondary auth and clean up
                        await secondaryAuth.signOut();
                        await deleteApp(secondaryApp);

                        // Step 2: Create user in local database (admin's token is still valid)
                        const response = await dataService.createUser(userObject);
                        if (!response) {
                            throw new Error("Failed to create user in local database.");
                        }
                        if (!response.success) {
                            setGeneralError(response.message || "Failed to create user in local database.");
                            return;
                        }

                        // Step 3: Send password reset email
                        await sendPasswordResetEmail(auth, email);

                        window.location.reload();
                    } catch (error) {
                        // Clean up secondary app on error
                        try { await deleteApp(secondaryApp); } catch (_) {}
                        throw error;
                    }

                } catch (error) {
                    console.error("Error creating user:", error);

                    if (error.code === "auth/email-already-in-use") {
                        // Try adding an existing user to another company
                        try {
                            const response = await dataService.createUser(userObject);

                            if (response.success) {
                                window.location.reload();
                            } else if (!response.success) {
                                setGeneralError(response.message);
                            } else {
                                setGeneralError(t('userCrudDialog.failedAssociateEmail'));
                            }
                        } catch (dbError) {
                            console.error("Database error:", dbError);
                            setGeneralError(t('userCrudDialog.unexpectedError'));
                        }
                    } else if (error.code === "auth/invalid-email") {
                        setEmailError(t('userCrudDialog.emailInvalid'));
                    } else {
                        setEmailError(t('userCrudDialog.unexpectedError'));
                    }
                }
            }
        } catch (error) {
            console.error("Unexpected error in handleSaveUser:", error);
            setGeneralError(t('userCrudDialog.unexpectedError'));
        }
    };

    const handleToggleSelection = (state) => {
        setGeneralError(null);
        setIsExistingUser(state);
    }

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            aria-labelledby="draggable-dialog-title"
            onClose={closeDialog}
            classes={{ paper: classes.dialogPaper }}
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {t('userCrudDialog.addUser')}
                </DialogTitle>
                <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <div className={classes.toggleFilter}>
                        <button onClick={() => handleToggleSelection(false)} style={{ width: "50%" }} className={classes.toggleFilter__optionButton}>
                            <p className={classes.toggleFilter__label}>{t('userCrudDialog.newUser')}</p>
                        </button>
                        <button onClick={() => handleToggleSelection(true)} style={{ width: "50%" }} className={classes.toggleFilter__optionButton}>
                            <p className={classes.toggleFilter__label}>{t('userCrudDialog.existingTaplrUser')}</p>
                        </button>
                        <div style={{ transform: !isExistingUser ? "translateX(0px)" : "translateX(100%)" }} className={classes.toggleSelected} />
                    </div>
                    {generalError &&
                        <div className={classes.errorContainer}>
                            <p className={classes.errorMessage}>{generalError}</p>
                        </div>
                    }
                    <div className={classes.form}>
                        {!isExistingUser &&
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                                <div className={classes.inputContainer}>
                                    <label className={classes.labelText}>{t('userCrudDialog.firstNameLabel')}</label>
                                    <TextField
                                        className={classes.textField}
                                        type={"text"}
                                        variant="outlined"
                                        size="small"
                                        onChange={(event) => changeFirstName(event)}
                                        value={firstName}
                                        fullWidth={true}
                                        error={firstNameError !== null}
                                        helperText={firstNameError}
                                    >
                                    </TextField>
                                </div>
                                <div className={classes.inputContainer}>
                                    <span className={classes.labelText}>{t('userCrudDialog.lastNameLabel')}</span>
                                    <TextField
                                        className={classes.textField}
                                        type={"text"}
                                        variant="outlined"
                                        size="small"
                                        onChange={(event) => changeSurName(event)}
                                        value={surName}
                                        fullWidth={true}
                                        error={surNameError !== null}
                                        helperText={surNameError}
                                    >
                                    </TextField>
                                </div>
                            </div>
                        }
                        <div className={classes.inputContainer}>
                            <span className={classes.labelText}>{t('userCrudDialog.emailLabel')}</span>
                            <TextField
                                className={classes.textField}
                                type={"text"}
                                variant="outlined"
                                size="small"
                                error={emailError !== null}
                                helperText={emailError}
                                onChange={(event) => changeEmail(event)}
                                value={email}
                                fullWidth={true}
                            >
                            </TextField>
                        </div>

                        <div className={classes.inputContainer} style={{ display: 'none', flexDirection: 'column' }}>
                            <span className={classes.labelText}>{t('userCrudDialog.rolesLabel')}</span>
                            <ToggleButtonGroup sx={{ width: '100% !important' }} onChange={(event) => changeRole(event)} value={roles}>
                                {props.roles.map((role, roleIndex) => {
                                    return <ToggleButton key={`toggleRoleIndex_${roleIndex}`} value={role.id} sx={{ width: "33.33%", textWrap: "nowrap", backgroundColor: roles.includes(role.id) && 'var(--color-primary) !important', color: roles.includes(role.id) && 'white !important' }}>{RoleNames.find(x => x.id === role.id).name}</ToggleButton>
                                })}
                            </ToggleButtonGroup>
                            {rolesError !== null && <p style={{ color: '#d32f2f', fontSize: "0.75rem", fontWeight: '400', letterSpacing: '0.03333em', marginLeft: '14px', marginTop: '4px', fontFamily: 'Roboto' }}>{rolesError}</p>}
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '16px', paddingRight: '16px', paddingBottom: "16px" }}>
                <Button variant="outlined" onClick={closeDialog}>{t('common.cancel')}</Button>
                <Button disabled={checkFields()} onClick={() => handleSaveUser()}>{isExistingUser ? t('userCrudDialog.addUser') : t('userCrudDialog.createUser')}</Button>
            </DialogActions>

        </Dialog >
    );
}


const useStyles = makeStyles(() => ({
    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '500px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: '#4654FB auto 1px',
        },
    },

    dialog__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        color: '#0C0E29',

        margin: '0px 0px 16px 0px',
    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "16px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    toggleFilter: {
        width: "100%",
        height: "32px",
        boxSizing: "border-box",

        position: "relative",

        display: "flex",
        alignItems: "center",

        backgroundColor: "#DCDCDC",
        borderRadius: "4px",

        padding: "1px",
        marginBottom: "16px",
    },

    toggleFilter__optionButton: {
        height: "100%",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",

        border: "none",
        borderRadius: "3px",

        color: "#0C0E29",
        backgroundColor: "transparent",

        transition: "all ease-in-out 0.2s",

        zIndex: "1",

        "&:hover": {
            // backgroundColor: "#DCDCDC",
            cursor: "pointer",
        },

    },

    toggleFilter__label: {
        fontFamily: "Lato, sans-serif",
        fontSize: "14px",
        margin: "0",
    },

    toggleSelected: {
        width: "calc(50% - 1px)",
        height: "30px",
        position: "absolute",
        transition: "transform 0.2s",

        backgroundColor: "white",
        borderRadius: "3px"
    },

    errorContainer: {
        width: "100%",
        backgroundColor: "#FDF3F3",

        border: "1px solid #FBE5E5",
        borderRadius: "6px",

        marginBottom: "8px",
        padding: "12px",
    },

    errorMessage: {
        fontSize: "16px",
        color: "#D84949"
    },

    form: {
        width: "100%",

        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },

    inputContainer: {
        width: "100%",

        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },

    saveButton: {
        border: "1px solid var(--color-primary) !important",
        background: "var(--color-primary) !important",
        display: 'flex',
        color: 'white !important',
        justifyContent: 'center',
        alignItems: 'center',

        "&:hover": {
            background: "rgba(195, 153, 107, 0.85) !important",

        },

        "&:disabled": {
            background: "#bdb6ac !important",
            border: "1px solid grey !important",
            color: 'white !important',
            cursor: 'not-allowed'
        },
    },

    cancelButton: {
        border: "1px solid var(--color-primary) !important",
        background: 'white',
        display: 'flex',
        color: 'var(--color-primary) !important',
        justifyContent: 'center',
        alignItems: 'center',

        "&:hover": {
            background: "rgba(195, 153, 107, 0.85) !important",

        },
    },
}));
